import type { Map as MapLibreMap } from 'maplibre-gl'
import { SLOT_RELIEF } from './slots'
import {
  RELIEF_FADE_MS,
  RELIEF_LAYER_ID,
  RELIEF_MANIFEST_PATH,
  RELIEF_MAX_ZOOM,
  RELIEF_SOURCE_ID,
  RELIEF_TILE_PATH,
  RELIEF_TILE_SIZE,
  reliefOpacityStops,
} from '../../lib/map_config'

/**
 * The relief: a pre-toned hillshade of this app's own, drawn over the paper and
 * under everything else (`RELIEF_SOURCE_ID` in map_config.ts says what it is).
 * This file is when it is added, and what it is allowed to ask for.
 *
 * AFTER THE MAP'S FIRST IDLE, NEVER BEFORE. The source is in neither style. It
 * is added once the plate has drawn — tiles in, names placed, nothing moving —
 * so there is no ordering of requests in which a hillshade delays an
 * appellation. The hills then develop under a map that is already there.
 *
 * NOT FOR A READER SAVING DATA. `Save-Data` (`navigator.connection.saveData`)
 * and `prefers-reduced-data` are both a reader saying that an image whose only
 * job is to be beautiful is not worth their bytes. They get the plate without
 * it, and nothing here makes a request.
 *
 * IT ASKS ONLY FOR IMAGES THAT EXIST. The pyramid has holes by design: the
 * world is baked to a shallow level and the deep levels only where there is an
 * appellation. A plain raster source would find the holes by asking — and a
 * browser writes every 404 it is answered to the console, whatever the page
 * does with the response, on top of the round trip itself: five of them, one
 * per level, for every tile of a view over ground nobody baked. So the map
 * reads the pyramid's MANIFEST first, one small document beside the images, and
 * its tiles go through a protocol of this app's own (`loadReliefTile`) that
 * answers "there is no such image" for anything the manifest does not list,
 * without a request. MapLibre takes that answer the way it takes a 404 — in
 * silence, no `error` event — and draws the level above, stretched, in that
 * tile's place; so an unbaked valley is a softer hillshade, never a hole.
 *
 * AND IT FADES WHERE ITS IMAGES RUN OUT. An unbaked valley is a softer
 * hillshade only up to a point: the world is baked to level 5, and at zoom 8
 * over the Alps that level is stretched twelve times and is no longer a
 * picture of anything. So how strong the layer is, and where it ends, follows
 * the deepest level the manifest lists for the ground in view
 * (`reliefOpacityStops` in map_config.ts, and `settleRelief` below, which is
 * when that is looked up: as the camera comes to rest, never per frame).
 *
 * With no manifest there is no relief: nothing is asked for that is not known
 * to be there. (The request for the manifest is the one request here that
 * COULD be answered 404, and a browser would say so in its console. So the
 * server never does: where nobody has baked a pyramid — a fresh checkout, CI,
 * the benchmark — it answers with a manifest that lists nothing
 * (`ReliefTiles`, lib/middleware), which adds nothing to the map.) The bake
 * writes the manifest (`Atlas::Relief::Manifest`), and it is this, as JSON:
 *
 *     {
 *       "complete": 5,
 *       "tiles": { "9": { "88": [[204, 206], 209] }, "10": { "176": [[409, 412]] } },
 *       "revision": "2026-10-01"
 *     }
 *
 * - `complete`: every image at this level and every level above it exists (the
 *   world pyramid). Absent, nothing is taken to be complete.
 * - `tiles`: the images beyond that, as level → column (`x`) → rows (`y`), a row
 *   being one number or an inclusive `[from, to]` run.
 * - `revision`: optional, anything — the bake writes the number of the recipe
 *   the images were shaded to; appended to every image's address as `?v=`, so
 *   a pyramid re-baked to a new recipe is not answered from a year-long cache.
 *
 * Types are imported from maplibre-gl, values never — this module is reached
 * only through use_maplibre.ts, which registers the protocol with the library
 * and is itself behind the layout's dynamic import.
 */

// The scheme the relief's tiles are asked for under. Not `http`: that is what
// puts every request for one through `loadReliefTile`.
export const RELIEF_PROTOCOL = 'atlas-relief'

const TILE_ADDRESS = new RegExp(`^${RELIEF_PROTOCOL}://(\\d+)/(\\d+)/(\\d+)$`)

type Run = [number, number]

/** Which images exist, read from the manifest. */
export interface ReliefCoverage {
  complete: number
  columns: Map<number, Map<number, Run[]>>
  revision: string | null
}

const whole = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0

/**
 * A manifest, checked rather than trusted: it is a document from a URL, and
 * what it says goes straight into which requests the map makes. Anything that
 * is not the shape above is ignored piece by piece — a malformed row costs that
 * row — and a document that is not an object at all is no manifest.
 */
export function reliefCoverage(manifest: unknown): ReliefCoverage | null {
  if (typeof manifest !== 'object' || manifest === null || Array.isArray(manifest)) return null

  const { complete, tiles, revision } = manifest as Record<string, unknown>
  const columns = new Map<number, Map<number, Run[]>>()

  if (typeof tiles === 'object' && tiles !== null) {
    for (const [level, byColumn] of Object.entries(tiles as Record<string, unknown>)) {
      if (typeof byColumn !== 'object' || byColumn === null) continue

      const rowsByColumn = new Map<number, Run[]>()

      for (const [column, rows] of Object.entries(byColumn as Record<string, unknown>)) {
        if (!Array.isArray(rows)) continue

        const runs: Run[] = []
        for (const row of rows) {
          if (whole(row)) runs.push([row, row])
          else if (Array.isArray(row) && row.length === 2 && whole(row[0]) && whole(row[1])) runs.push([row[0], row[1]])
        }

        if (runs.length > 0) rowsByColumn.set(Number(column), runs)
      }

      if (rowsByColumn.size > 0) columns.set(Number(level), rowsByColumn)
    }
  }

  return {
    complete: whole(complete) ? complete : -1,
    columns,
    revision: typeof revision === 'string' && revision !== '' ? revision : null,
  }
}

// Whether the manifest lists any image at all.
const baked = (coverage: ReliefCoverage) => coverage.complete >= 0 || coverage.columns.size > 0

/** Whether the pyramid holds the image for a tile. */
export function reliefCovers(coverage: ReliefCoverage, level: number, column: number, row: number): boolean {
  if (level <= coverage.complete) return true

  const runs = coverage.columns.get(level)?.get(column)

  return runs !== undefined && runs.some(([from, to]) => row >= from && row <= to)
}

// What the protocol answers from. One per page rather than per map: the
// protocol is registered with the library, not with a map, and the pyramid is
// the same whichever map is asking.
let coverage: ReliefCoverage | null = null
let asked: Promise<ReliefCoverage | null> | null = null

/** For a test: as if the page had just loaded. */
export function forgetRelief() {
  coverage = null
  asked = null
}

// "There is no such image", in the one form MapLibre takes in silence: an
// error whose `status` is 404 fires no `error` event and sends it to the level
// above.
function absent() {
  return Object.assign(new Error('No relief image is baked for this tile'), { status: 404 })
}

function imageAddress(level: string, column: string, row: string, revision: string | null) {
  const path = RELIEF_TILE_PATH.replace('{z}', level).replace('{x}', column).replace('{y}', row)

  return revision ? `${path}?v=${encodeURIComponent(revision)}` : path
}

/**
 * The relief's protocol: one tile, by its `atlas-relief://z/x/y` address.
 *
 * Answers from the manifest before it answers from the network. A tile the
 * manifest does not list is refused here, with no request; one it does list is
 * fetched from this origin. Should the server then say otherwise — the pyramid
 * changed under a manifest a browser had cached — that is passed on as what it
 * is, and a 404 among them is still silent.
 */
export async function loadReliefTile(request: { url: string }, abort: AbortController) {
  const address = TILE_ADDRESS.exec(request.url)
  if (!address || !coverage) throw absent()

  const [, level, column, row] = address
  if (!reliefCovers(coverage, Number(level), Number(column), Number(row))) throw absent()

  const response = await fetch(imageAddress(level, column, row, coverage.revision), { signal: abort.signal })
  if (!response.ok) {
    throw Object.assign(new Error(`The relief image answered ${response.status}`), { status: response.status })
  }

  return {
    data: await response.arrayBuffer(),
    cacheControl: response.headers.get('Cache-Control'),
    expires: response.headers.get('Expires'),
  }
}

/**
 * Whether this reader is to be sent the relief at all.
 *
 * Asked when the relief is about to be added rather than once at load: a
 * setting changed mid-visit is honoured by the next map, and nothing is read
 * during a server render, where there is no navigator to ask.
 */
export function reliefWanted(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false

  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  if (connection?.saveData === true) return false

  return !(window.matchMedia?.('(prefers-reduced-data: reduce)').matches ?? false)
}

// The manifest, asked for once per page. Any failure — no such document, a
// server error, a body that is not JSON, no network — is the same answer: there
// is no relief to draw, and that is not an error anybody is owed.
//
// `no-cache` is "ask the server whether my copy is still good", not "do not
// cache": the images sit under a path served with a year-long lifetime, and a
// manifest answered from that cache would go on describing last year's pyramid.
// It costs a conditional request, after the map has drawn, and a 304 — the
// server sends the manifest with an ETag and the same directive.
function askForCoverage(): Promise<ReliefCoverage | null> {
  asked ??= fetch(RELIEF_MANIFEST_PATH, { cache: 'no-cache', headers: { accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((manifest: unknown) => {
      coverage = reliefCoverage(manifest)

      return coverage
    })
    .catch(() => null)

  return asked
}

const zoomRamp = (...stops: number[]) => ['interpolate', ['linear'], ['zoom'], ...stops]

/** A camera frame, as far as the relief needs one: the ground on screen. */
export interface ReliefFrame {
  west: number
  south: number
  east: number
  north: number
}

const clampLatitude = (latitude: number) => Math.max(-85.0511, Math.min(85.0511, latitude))

// Where a longitude and a latitude are on the Web Mercator square, 0 to 1.
const across = (lng: number) => (lng + 180) / 360
const down = (lat: number) => 0.5 - Math.log(Math.tan(Math.PI / 4 + (clampLatitude(lat) * Math.PI) / 360)) / (2 * Math.PI)

/**
 * The deepest level the pyramid holds an image at for ANY part of a frame:
 * `complete` where only the world pyramid covers it, up to `RELIEF_MAX_ZOOM`
 * over an appellation.
 *
 * ANY part, not most of it: a view half over Napa and half over the Central
 * Valley keeps its relief for Napa's sake, and the valley beside it is the
 * softer version it always was. Only a view with no deep image anywhere in it
 * gives the relief up early.
 *
 * Exact, and cheap. From the deepest level down, the frame is a rectangle of
 * tiles and the manifest is columns of row runs; the first level at which the
 * two overlap is the answer. It walks whichever is fewer — the frame's
 * columns, or the columns the manifest lists at that level — so the world view
 * costs a few hundred comparisons and a valley costs four.
 */
export function deepestLevel(known: ReliefCoverage, frame: ReliefFrame): number {
  const deepest = Math.max(known.complete, 0)

  for (let level = RELIEF_MAX_ZOOM; level > deepest; level--) {
    const columns = known.columns.get(level)
    if (!columns) continue

    const tiles = 2 ** level
    const tile = (share: number) => Math.min(tiles - 1, Math.max(0, Math.floor(share * tiles)))
    const west = tile(across(frame.west))
    const east = tile(across(frame.east))
    const north = tile(down(frame.north))
    const south = tile(down(frame.south))
    const inRows = (runs: Run[]) => runs.some(([from, to]) => from <= south && to >= north)

    if (east - west < columns.size) {
      for (let column = west; column <= east; column++) {
        const runs = columns.get(column)
        if (runs && inRows(runs)) return level
      }
    } else {
      for (const [column, runs] of columns) {
        if (column >= west && column <= east && inRows(runs)) return level
      }
    }
  }

  return deepest
}

// The level each map's relief is currently drawn for, so a settle on ground of
// the same depth sets nothing. Weak: a removed map takes its entry with it.
const drawnFor = new WeakMap<MapLibreMap, number>()

function frameOf(map: MapLibreMap): ReliefFrame {
  const bounds = map.getBounds()

  return { west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth() }
}

/**
 * Sets the relief's strength for the ground a frame holds — the map's own
 * frame when none is given — if that differs from what it is drawn for.
 *
 * Called when the camera comes to rest, and once more as a flight begins, for
 * the frame it will land in: a flight from the world view into Temecula would
 * otherwise cross the last three zooms with the relief faded for the open
 * ocean it left. It reads the manifest already in memory and, when the depth
 * has changed, sets one paint property and the layer's range; MapLibre eases
 * between the two over its usual third of a second. Nothing when the relief is
 * not on the map.
 */
export function settleRelief(map: MapLibreMap, frame?: ReliefFrame) {
  if (!coverage || !map.getLayer(RELIEF_LAYER_ID)) return

  const deepest = deepestLevel(coverage, frame ?? frameOf(map))
  if (drawnFor.get(map) === deepest) return

  const stops = reliefOpacityStops(deepest)

  drawnFor.set(map, deepest)
  // The range first when it grows, so the layer exists at the zoom the new
  // ramp is about to give it a strength at.
  map.setLayerZoomRange(RELIEF_LAYER_ID, 0, stops[stops.length - 2])
  map.setPaintProperty(RELIEF_LAYER_ID, 'raster-opacity', zoomRamp(...stops) as never)
}

/**
 * Puts the relief on a map whose style has it not, under the relief slot.
 *
 * `tileSize` is larger than the images are: a 512 px image over a 768 px tile
 * is always drawn a little soft, and a view needs about half the images a
 * 512 px grid would. The layer ends where its opacity reaches nothing, so past
 * that zoom the source is unused and asks for nothing — and both are set for
 * the ground the map is showing as the layer is added (`deepestLevel`).
 */
export function addRelief(map: MapLibreMap) {
  if (map.getSource(RELIEF_SOURCE_ID) || !map.getLayer(SLOT_RELIEF)) return

  const deepest = coverage ? deepestLevel(coverage, frameOf(map)) : RELIEF_MAX_ZOOM
  const stops = reliefOpacityStops(deepest)

  map.addSource(RELIEF_SOURCE_ID, {
    type: 'raster',
    tiles: [`${RELIEF_PROTOCOL}://{z}/{x}/{y}`],
    tileSize: RELIEF_TILE_SIZE,
    maxzoom: RELIEF_MAX_ZOOM,
  })

  map.addLayer(
    {
      id: RELIEF_LAYER_ID,
      type: 'raster',
      source: RELIEF_SOURCE_ID,
      maxzoom: stops[stops.length - 2],
      paint: {
        'raster-opacity': zoomRamp(...stops) as never,
        'raster-resampling': 'linear',
        'raster-fade-duration': RELIEF_FADE_MS,
      },
    },
    SLOT_RELIEF,
  )

  drawnFor.set(map, deepest)
}

/**
 * Arranges for the relief to be added once the map has come to rest for the
 * first time since its style loaded. Called from `style.load`, so a style swap
 * arranges it again for the new style.
 *
 * `current` says whether this map is still the one on the page and still on the
 * style this was called for: the manifest is a request, and by the time it is
 * answered the reader may have left for the journal.
 */
export function addReliefWhenIdle(map: MapLibreMap, current: () => boolean) {
  if (!reliefWanted()) return

  map.once('idle', () => {
    if (!current()) return

    void askForCoverage().then((known) => {
      // A manifest that lists nothing is a pyramid nobody has baked yet — what
      // a fresh checkout has. It gets no source: a layer every tile of which
      // would be refused is work for a picture of nothing.
      if (!known || !baked(known) || !current()) return

      addRelief(map)
    })
  })
}
